import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly STORAGE_KEY = 'mbs-theme';

  readonly isDark = signal<boolean>(false);

  constructor() {
    const saved = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    // matchMedia isn't implemented in jsdom (jest env) — fall back to light when unavailable.
    const prefersDark = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.apply((saved ?? (prefersDark ? 'dark' : 'light')) === 'dark');
  }

  toggle(): void {
    this.apply(!this.isDark());
  }

  // Apply an explicit theme (used when syncing from server-side user settings).
  setTheme(theme: Theme): void {
    this.apply(theme === 'dark');
  }

  private apply(dark: boolean): void {
    this.isDark.set(dark);
    // data-bs-theme
    this.document.documentElement.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
    localStorage.setItem(this.STORAGE_KEY, dark ? 'dark' : 'light');
  }
}
